import React from "react";
import { motion } from "framer-motion";
import { ArrowDownRight } from "lucide-react";
import { useTheme } from "../../context/ThemeContext";
import { getTierNames } from "../../utils/tierNames";
import { sectionBgColor } from "../../utils/themeBg";

const HERO_BG =
  "https://static.prod-images.emergentagent.com/jobs/0ea60aca-0e0a-481c-bb77-149fc760bfd5/images/0d0a9775d16f4bf5d8b3f3f460aed0612ecbc494ec0741243b2b297856dce85b.png";

const LOGO_URL =
  "https://customer-assets.emergentagent.com/wingman/7b8b7474-e52e-4388-adc0-a2361520c0a4/attachments/8840390a845949e6b9092405676c1743_image.png";

const SPACING_STYLES = {
  compact: { paddingTop: "4rem", paddingBottom: "4rem" },
  spacious: { paddingTop: "8rem", paddingBottom: "10rem" },
};

const Hero = () => {
  const { settings } = useTheme();
  const tierNames = getTierNames(settings);
  const bgImage = settings?.hero?.bg_image || HERO_BG;
  const logoUrl = settings?.logo_url || LOGO_URL;
  const isCentered = settings?.layout?.hero_align === "center";
  const containerWidth = Number(settings?.layout?.container_width) || 1280;
  const spacingStyle = SPACING_STYLES[settings?.layout?.section_spacing] || {};

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <section
      id="hero"
      data-testid="hero-section"
      className="lv-hero relative w-full min-h-[92vh] overflow-hidden"
      style={{ backgroundColor: sectionBgColor(settings, "hero") }}
    >
      {/* Background image */}
      <div
        className="absolute inset-0 bg-cover bg-center"
        style={{ backgroundImage: `url(${bgImage})` }}
      />
      {/* Gradient overlays */}
      <div
        className="absolute inset-0"
        style={{ backgroundImage: "linear-gradient(to right, var(--lv-bg,#060A14), color-mix(in srgb, var(--lv-bg,#060A14) 80%, transparent), transparent)" }}
      />
      <div
        className="absolute inset-0"
        style={{ backgroundImage: "linear-gradient(to top, var(--lv-bg,#060A14), transparent, color-mix(in srgb, var(--lv-bg,#060A14) 40%, transparent))" }}
      />
      {/* Grain */}
      <div
        className="absolute inset-0 opacity-[0.04] pointer-events-none"
        style={{
          backgroundImage:
            "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")",
        }}
      />

      <div
        className={`relative mx-auto px-4 sm:px-6 pt-24 pb-24 md:pt-32 md:pb-40 min-h-[92vh] flex flex-col justify-center ${isCentered ? "items-center text-center" : ""}`}
        style={{ maxWidth: `${containerWidth}px`, ...spacingStyle }}
      >
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.1 }}
          className={`flex items-center gap-5 mb-8 ${isCentered ? "justify-center" : ""}`}
          data-testid="hero-logo"
        >
          <img
            src={logoUrl}
            alt="LEOVERA Nutrition"
            className="h-16 sm:h-20 w-auto object-contain rounded-md bg-white p-2 shadow-[0_0_0_1px_rgba(192,192,192,0.3)]"
          />
          <div className="flex flex-col">
            <span className="h-px w-12 bg-[#C0C0C0] mb-2" />
            <span className="text-[11px] tracking-[0.38em] uppercase text-[#C0C0C0]">
              Sport Nutrition · V104.3
            </span>
          </div>
        </motion.div>

        <motion.h1
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className={`font-[Outfit] text-white tracking-tight font-light leading-[0.95] max-w-4xl lv-hero-title ${isCentered ? "mx-auto" : ""}`}
          style={{
            fontSize: "clamp(2.75rem, 7vw, 5.75rem)",
          }}
          data-testid="hero-headline"
        >
          Forza naturale.{" "}
          <span className="block italic font-extralight text-[#C0C0C0]">
            Precisione d'élite.
          </span>
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.35 }}
          className={`mt-8 max-w-xl font-[Manrope] text-[#E5E4E2]/90 text-base sm:text-lg leading-relaxed lv-hero-subtitle ${isCentered ? "mx-auto" : ""}`}
          data-testid="hero-mission"
        >
          LEOVERA: Eccellenza nella nutrizione per massimizzare le tue
          performance, dalla base a {tierNames.elite.name}.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.5 }}
          className={`mt-12 flex flex-wrap items-center gap-4 ${isCentered ? "justify-center" : ""}`}
        >
          <button
            onClick={() => scrollTo("start")}
            data-testid="hero-cta-explore"
            className="lv-hero-cta group inline-flex items-center gap-3 bg-[#C0C0C0] text-[#060A14] px-8 py-4 rounded-sm text-[12px] tracking-[0.28em] uppercase font-medium hover:bg-white transition-all duration-300"
          >
            Esplora il catalogo
            <ArrowDownRight className="w-4 h-4 group-hover:translate-y-0.5 group-hover:translate-x-0.5 transition-transform" />
          </button>
          <button
            onClick={() => scrollTo("brand")}
            data-testid="hero-cta-brand"
            className="lv-hero-cta inline-flex items-center gap-2 px-8 py-4 border border-white/20 text-white hover:border-[#C0C0C0] hover:text-[#C0C0C0] transition-all duration-300 text-[12px] tracking-[0.28em] uppercase rounded-sm"
          >
            La nostra filosofia
          </button>
        </motion.div>

        {/* Three pillars footer of hero */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 0.7 }}
          className={`mt-20 grid grid-cols-3 gap-3 sm:gap-10 max-w-2xl border-t border-white/10 pt-8 ${isCentered ? "mx-auto" : ""}`}
        >
          {[
            { k: "01", label: tierNames.start.name },
            { k: "02", label: tierNames.power.name },
            { k: "03", label: tierNames.elite.name },
          ].map((t) => (
            <div key={t.k} className="flex flex-col min-w-0">
              <span className="text-[10px] tracking-[0.32em] uppercase text-[#94A3B8]">
                {t.k}
              </span>
              <span
                className="mt-2 font-[Outfit] text-white uppercase leading-tight whitespace-nowrap"
                style={{ fontSize: "clamp(9px, 3vw, 18px)", letterSpacing: "clamp(0, 0.6vw, 0.18em)" }}
              >
                {t.label}
              </span>
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export default Hero;
